import { CSSProperties } from 'react'
import { Row, Tooltip } from 'antd'
import ActivityLogs from '@Quotes/components/ActivityLogs'
import { PURCHASE_ORDER } from '@Orders/constants'
import { formatDateOrder } from '@Orders/logic'
import { PreferredPricingIconTooltip } from '@Orders/OrderDetailsPage/components'
import { salesOrder_salesOrder_paymentDetails as SalesOrderPaymentDetailsType } from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrder'
import { PROVISIONED_UI_FLAGS } from '@Builder/QuoteDetailPage/logic'

import Box from '~/core/Box'
import { getPriceDisplay } from '~/lib/getPriceDisplay'
import getColor from '~/lib/getColor'
import ProjectSecurityCertificate from '~/apps/consumer/components/ProjectSafetyCertificate'
import { useFlagEnabled } from '~/lib/isFlagEnabled'

import { SalesOrderInfo } from './SalesOrderInfo'

const ORDER_INFO_CONTENT: CSSProperties = {
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '28px',
    whiteSpace: 'normal',
    color: getColor('blackAlpha65'),
}

const MARGIN_ABP_DISCOUNT_ICON = '6px'

const BOX_WIDTHS = {
    base: {
        orderInfo: '225px',
        orderTotal: '225px',
        securePlus: '0',
        orderPlaced: '225px',
        teamWorkspace: '300px',
        depositPlaced: '225px',
    },
    securePlus: {
        orderInfo: '287px',
        securePlus: '330px',
        orderTotal: '224px',
        orderPlaced: '330px',
        teamWorkspace: '400px',
        depositPlaced: '0',
    },
}

const securityCertificateExtraStyles = {
    icon: {
        fontSize: '22px',
    },
    text: {
        fontWeight: 700,
        fontSize: '20px',
        color: getColor('blackAlpha65'),
    },
}
const CHARACTER_LENGTH = 15

type PostalAddress = {
    id: string
    name: string
    street: string
    street2: string | null
    city: string
    state: string
    postalCode: string
    country: string
    phone: string | null
}

type BasicUser = {
    id: number
    displayName: string
}

export type PaymentDetailsType = {
    paidBy: BasicUser | null
    paidByDisplayName: string | null
    billingAddress: PostalAddress
    purchaseOrderUri: string | null
    paymentType: string | null
    purchaseOrderNumber: string | null
}

export interface OrderSummaryHeaderPropsType {
    createdAt?: Date
    deliveryStatus?: string
    depositAmount?: number | null
    isImOrder: boolean
    name: string
    paymentDetails: PaymentDetailsType | SalesOrderPaymentDetailsType | null
    project: {
        name: string
    } | null
    saleOrderId: string
    shipmentTotal?: number
    shippedBy?: Date
    totalAmount: number
    isBusinessAccountDiscountApplied: boolean | null
    appliedBusinessAccountDiscountPercent: number | null
    team:
        | {
              name: string
          }
        | null
        | undefined
    salesOrder: any
}

export const OrderSummaryHeader = (props: OrderSummaryHeaderPropsType) => {
    const {
        createdAt,
        depositAmount,
        isImOrder,
        name,
        paymentDetails,
        project,
        saleOrderId,
        totalAmount,
        isBusinessAccountDiscountApplied,
        appliedBusinessAccountDiscountPercent,
        team,
        salesOrder,
    } = props

    const { paymentType } = paymentDetails || {}

    const orderTotal = getPriceDisplay(totalAmount)
    const depositedAmount = getPriceDisplay(depositAmount)
    const orderPlacedDate = createdAt && formatDateOrder(createdAt)

    const isCpqFlagV2Enabled = useFlagEnabled(
        PROVISIONED_UI_FLAGS.UI_CPQ_QUOTES_INTEGRATION_V2,
    )

    const secureProjectName = project?.name

    const widths = secureProjectName ? BOX_WIDTHS.securePlus : BOX_WIDTHS.base

    const showDepositValue =
        paymentType === PURCHASE_ORDER &&
        isImOrder &&
        !(isCpqFlagV2Enabled && salesOrder?.quotePdfUrl)

    return (
        <Row gutter={[0, 16]}>
            <Box width={widths.orderInfo} style={{ marginRight: '24px' }}>
                <SalesOrderInfo title="Order ID">
                    <Box
                        style={ORDER_INFO_CONTENT}
                        data-test-target="order-details-order-id"
                    >
                        <Tooltip title={name}>
                            {name?.length > CHARACTER_LENGTH
                                ? `${name.substring(0, CHARACTER_LENGTH)}...`
                                : name}
                        </Tooltip>
                    </Box>
                </SalesOrderInfo>
            </Box>
            {secureProjectName && (
                <Box
                    direction="col"
                    width={widths.securePlus}
                    style={{ marginRight: '24px' }}
                >
                    Project
                    <ProjectSecurityCertificate
                        projectName={secureProjectName}
                        extraStyles={securityCertificateExtraStyles}
                        iconTooltip={{
                            title: 'This is a Secure+ Project',
                        }}
                    />
                </Box>
            )}
            <Box width={widths.orderTotal} style={{ marginRight: '24px' }}>
                <SalesOrderInfo title="Order total">
                    <Box>
                        {isBusinessAccountDiscountApplied && (
                            <PreferredPricingIconTooltip
                                discount={appliedBusinessAccountDiscountPercent}
                                marginTop={MARGIN_ABP_DISCOUNT_ICON}
                            />
                        )}
                        <Box
                            style={ORDER_INFO_CONTENT}
                            data-test-target="order-details-order-total"
                        >
                            {orderTotal}
                        </Box>
                    </Box>
                </SalesOrderInfo>
            </Box>
            {showDepositValue && (
                <Box
                    width={widths.depositPlaced}
                    style={{ marginRight: '24px' }}
                >
                    <SalesOrderInfo title="Deposit amount">
                        <Box
                            style={ORDER_INFO_CONTENT}
                            data-test-target="order-details-order-deposit"
                        >
                            {depositedAmount}
                        </Box>
                    </SalesOrderInfo>
                </Box>
            )}
            {isCpqFlagV2Enabled && salesOrder?.quotePdfUrl ? (
                <Box width={widths.orderPlaced} style={{ marginRight: '24px' }}>
                    <SalesOrderInfo title={'Order placed'}>
                        <Box
                            style={ORDER_INFO_CONTENT}
                            data-test-target="order-details-order-placed"
                        >
                            {orderPlacedDate}
                        </Box>
                    </SalesOrderInfo>
                </Box>
            ) : (
                <Box width={widths.orderPlaced} style={{ marginRight: '24px' }}>
                    <SalesOrderInfo
                        title={
                            showDepositValue
                                ? 'Deposit paid on'
                                : 'Order placed'
                        }
                    >
                        <Box
                            style={ORDER_INFO_CONTENT}
                            data-test-target="order-details-order-placed"
                        >
                            {orderPlacedDate}
                        </Box>
                    </SalesOrderInfo>
                </Box>
            )}
            {team && (
                <Box width={widths.orderPlaced} style={{ marginRight: '24px' }}>
                    <SalesOrderInfo title="Team workspace">
                        <Box
                            style={ORDER_INFO_CONTENT}
                            data-test-target="order-details-team-workspace"
                        >
                            {team?.name}
                        </Box>
                    </SalesOrderInfo>
                </Box>
            )}
            <Box alignItems="end" margin="auto 0 auto auto">
                <ActivityLogs saleOrderId={saleOrderId} />
            </Box>
        </Row>
    )
}

export default OrderSummaryHeader
